import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiError, createAdminUser } from '@/lib/api'

export function InviteUserModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const qc = useQueryClient()

  const create = useMutation({
    mutationFn: createAdminUser,
    onSuccess: (data) => {
      setGeneratedPassword(data.generated_password ?? '(password set by administrator)')
      setErrorMsg(null)
      qc.invalidateQueries({ queryKey: ['admin-users'] })
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error'
      setErrorMsg(msg)
    },
  })

  const handleClose = () => {
    setEmail('')
    setName('')
    setGeneratedPassword(null)
    setCopied(false)
    setErrorMsg(null)
    create.reset()
    onClose()
  }

  const handleCopy = async () => {
    if (!generatedPassword) return
    try {
      await navigator.clipboard.writeText(generatedPassword)
      setCopied(true)
    } catch {
      // Clipboard may not be available; ignore
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent>
        {!generatedPassword ? (
          <>
            <DialogHeader>
              <DialogTitle>Add user</DialogTitle>
              <DialogDescription>
                After creation, the generated password will be shown. Pass it to the user personally.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email</Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-name">Name</Label>
                <Input
                  id="invite-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="off"
                />
              </div>
              {errorMsg && (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                  {errorMsg}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  onClick={() => create.mutate({ email, name })}
                  disabled={!email || !name || create.isPending}
                >
                  {create.isPending ? 'Creating…' : 'Create'}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>User created</DialogTitle>
              <DialogDescription className="font-medium text-destructive">
                Password will be shown only once. Copy it now.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="break-all rounded bg-muted p-3 font-mono text-sm">
                {generatedPassword}
              </div>
              <Button
                onClick={handleCopy}
                variant={copied ? 'secondary' : 'default'}
                className="w-full"
              >
                {copied ? 'Copied' : 'Copy password'}
              </Button>
              <Button variant="outline" onClick={handleClose} className="w-full">
                Close
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
